import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import {
  FileText, Star, Pin, MoreVertical, Edit3, Copy, Trash2, RotateCcw,
  FolderInput, Bell, Share2,
} from 'lucide-react'
import {
  Badge,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@goodday-clone/ui'
import { cn } from '@/shared/utils/utils'
import type { Note, NoteFolder } from './notesApi'
import { authorName, departmentTagStyle, formatNoteTime, initialsOf, PRIORITY_LABELS } from './notesApi'

export interface CardActions {
  onToggleStar: (note: Note) => void
  onTogglePin: (note: Note) => void
  onDuplicate: (note: Note) => void
  onTrash: (note: Note) => void
  onRestore: (note: Note) => void
  onDeletePermanent: (note: Note) => void
  onMoveToFolder: (note: Note, folderId: string | null) => void
  onShare: (note: Note) => void
  folders: NoteFolder[]
  canEdit: (note: Note) => boolean
  canDelete: (note: Note) => boolean
}

export function NoteMenu({ note, actions }: { note: Note; actions: CardActions }) {
  const navigate = useNavigate()
  if (note.deletedAt) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button onClick={(e) => e.stopPropagation()} className="p-1 rounded hover:bg-slate-100 text-slate-400">
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); actions.onRestore(note) }} className="flex items-center gap-2 text-sm">
            <RotateCcw className="h-3.5 w-3.5" /> Restore
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); actions.onDeletePermanent(note) }} className="flex items-center gap-2 text-sm text-red-600">
            <Trash2 className="h-3.5 w-3.5" /> Delete forever
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button onClick={(e) => e.stopPropagation()} className="p-1 rounded hover:bg-slate-100 text-slate-400">
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/app/notepad/${note._id}`) }} className="flex items-center gap-2 text-sm">
          <Edit3 className="h-3.5 w-3.5" /> Open
        </DropdownMenuItem>
        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); actions.onToggleStar(note) }} className="flex items-center gap-2 text-sm">
          <Star className={cn('h-3.5 w-3.5', note.isStarred && 'fill-amber-400 text-amber-400')} />
          {note.isStarred ? 'Unstar' : 'Star'}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); actions.onTogglePin(note) }} className="flex items-center gap-2 text-sm">
          <Pin className={cn('h-3.5 w-3.5', note.isPinned && 'fill-emerald-600 text-emerald-600')} />
          {note.isPinned ? 'Unpin' : 'Pin to top'}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); actions.onShare(note) }} className="flex items-center gap-2 text-sm">
          <Share2 className="h-3.5 w-3.5" /> Share
        </DropdownMenuItem>
        {actions.folders.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={!actions.canEdit(note)}
              onClick={(e) => { e.stopPropagation(); actions.onMoveToFolder(note, null) }}
              className="flex items-center gap-2 text-sm"
            >
              <FolderInput className="h-3.5 w-3.5" /> Unfile
            </DropdownMenuItem>
            {actions.folders.map((f) => (
              <DropdownMenuItem
                key={f._id}
                disabled={!actions.canEdit(note)}
                onClick={(e) => { e.stopPropagation(); actions.onMoveToFolder(note, f._id) }}
                className="flex items-center gap-2 text-sm"
              >
                <FolderInput className="h-3.5 w-3.5" /> {f.name}
              </DropdownMenuItem>
            ))}
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); actions.onDuplicate(note) }} className="flex items-center gap-2 text-sm">
          <Copy className="h-3.5 w-3.5" /> Duplicate
        </DropdownMenuItem>
        {actions.canDelete(note) && (
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); actions.onTrash(note) }} className="flex items-center gap-2 text-sm text-red-600">
            <Trash2 className="h-3.5 w-3.5" /> Move to trash
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function Avatar({ name }: { name: string }) {
  return (
    <span
      className="h-6 w-6 shrink-0 rounded-full bg-slate-700 text-white text-[10px] font-semibold flex items-center justify-center shadow-xs"
      title={name}
    >
      {initialsOf(name)}
    </span>
  )
}

function contentPreview(note: Note) {
  return (note.content || '')
    .replace(/^#{1,3}\s+/gm, '')
    .replace(/[*_`>~-]/g, '')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\n+/g, ' ')
    .trim()
}

export function NoteCard({ note, actions }: { note: Note; actions: CardActions }) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const author = authorName(note, user?.id)
  const folder = typeof note.folderId === 'object' ? note.folderId : null
  const preview = contentPreview(note)

  return (
    <div
      onClick={() => navigate(`/app/notepad/${note._id}`)}
      className={cn(
        'group relative flex flex-col justify-between cursor-pointer bg-white border rounded-xl p-4 transition-all duration-150 overflow-hidden',
        'border-slate-200 hover:border-emerald-300 hover:shadow-sm',
        note.deletedAt && 'opacity-70'
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <span className="h-8 w-8 shrink-0 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
            {folder ? <FolderInput className="h-4 w-4 text-emerald-600" /> : <FileText className="h-4 w-4 text-emerald-600" />}
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-slate-900 text-sm truncate flex items-center gap-1.5" title={note.title || 'Untitled note'}>
              <span className="truncate">{note.title || 'Untitled note'}</span>
              {note.isPinned && <Pin className="h-3 w-3 text-emerald-600 fill-emerald-600 shrink-0" />}
            </h3>
          </div>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {note.reminder?.enabled && note.reminder.at && !note.deletedAt && (
            <span title="Reminder set" className="p-1 text-slate-400">
              <Bell className="h-3.5 w-3.5" />
            </span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); actions.onToggleStar(note) }}
            className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-amber-500 transition-colors"
            title={note.isStarred ? 'Unstar' : 'Star'}
          >
            <Star className={cn('h-4 w-4', note.isStarred ? 'fill-amber-400 text-amber-400' : 'text-slate-300 group-hover:text-slate-400')} />
          </button>
          <NoteMenu note={note} actions={actions} />
        </div>
      </div>

      {/* Body preview */}
      <p className="text-xs text-slate-500 line-clamp-2 min-h-[2.25rem] mb-3 flex-1 break-words">
        {preview || 'No content'}
      </p>

      {/* Metadata tags (Department, Tags, Priority) */}
      {(note.department || (note.tags && note.tags.length > 0) || (note.priority && note.priority !== 'LOW')) && (
        <div className="flex items-center gap-1.5 flex-wrap mb-2.5">
          {note.priority && note.priority !== 'LOW' && (
            <span className={cn(
              'px-2 py-0.5 rounded-full text-[10px] font-medium border truncate',
              note.priority === 'HIGH' ? 'bg-rose-50 text-rose-600 border-rose-200' : 'bg-amber-50 text-amber-600 border-amber-200'
            )}>
              {note.priority === 'HIGH' ? 'High' : 'Medium'}
            </span>
          )}
          {note.department && (
            <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-medium border truncate max-w-[130px]', departmentTagStyle(note.department))}>
              {note.department}
            </span>
          )}
          {note.tags?.slice(0, 2).map((t) => (
            <span key={t} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-sky-50 text-sky-700 border border-sky-200 truncate max-w-[100px]">
              {t}
            </span>
          ))}
        </div>
      )}

      {/* Footer: Date and Author Avatar */}
      <div className="pt-2.5 border-t border-slate-100 flex items-center justify-between gap-2 mt-auto min-w-0">
        <span className="text-[11px] text-slate-400 truncate min-w-0" title={formatNoteTime(note.updatedAt)}>
          {formatNoteTime(note.updatedAt)}
        </span>
        <div className="shrink-0 flex items-center" title={author}>
          <Avatar name={author} />
        </div>
      </div>
    </div>
  )
}

export function NoteRow({ note, actions }: { note: Note; actions: CardActions }) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const author = authorName(note, user?.id)
  const folder = typeof note.folderId === 'object' ? note.folderId : null

  return (
    <div
      onClick={() => navigate(`/app/notepad/${note._id}`)}
      className={cn(
        'group flex items-center gap-3 p-3 rounded-xl transition-colors cursor-pointer bg-white border',
        'border-slate-200 hover:border-emerald-300 hover:bg-slate-50/50',
        note.deletedAt && 'opacity-70'
      )}
    >
      <span className="h-9 w-9 shrink-0 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
        {folder ? <FolderInput className="h-4 w-4 text-emerald-600" /> : <FileText className="h-4 w-4 text-emerald-600" />}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-sm text-slate-900 truncate">{note.title}</h3>
          {note.isStarred && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400 shrink-0" />}
          {note.isPinned && <Pin className="h-3.5 w-3.5 fill-emerald-600 text-emerald-600 shrink-0" />}
        </div>
        <p className="text-xs text-slate-500 truncate">{contentPreview(note) || 'No content'}</p>
      </div>
      {note.priority && (
        <Badge variant="outline" className="hidden md:inline-flex shrink-0 border-rose-200 bg-rose-50 text-rose-600 text-[11px]">
          {PRIORITY_LABELS[note.priority]}
        </Badge>
      )}
      {note.department && (
        <span className={cn('hidden sm:inline-flex shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium border', departmentTagStyle(note.department))}>
          {note.department}
        </span>
      )}
      <span className="text-xs text-slate-400 shrink-0 hidden lg:block w-32 text-right">{formatNoteTime(note.updatedAt)}</span>
      <Avatar name={author} />
      <NoteMenu note={note} actions={actions} />
    </div>
  )
}
